<script setup lang="ts">
import type { SplitBtnItem } from '@/components/btns/KSplitBtn.vue';

import { ref } from 'vue';
import { VForm } from 'vuetify/components';

import JobBookingFooterBar from '@/components/bars/JobBookingFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import KSplitBtn from '@/components/btns/KSplitBtn.vue';
import DetailedJobAttachmentsDropZoneField from '@/components/fields/DetailedJobAttachmentsDropZoneField.vue';
import JobBookingErrorPanel from '@/components/panels/JobBookingErrorPanel.vue';
import PacPalletProductRow from '@/components/rows/PacPalletProductRow.vue';
import AdvancedBookingFormSection from '@/components/sections/AdvancedBookingFormSection.vue';
import JobBookingCustomerSection from '@/components/sections/JobBookingCustomerSection.vue';
import JobBookingProductsSection from '@/components/sections/JobBookingProductsSection.vue';
import JobBookingStopsSection from '@/components/sections/JobBookingStopsSection.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useJobBookingBookFlow from '@/composables/jobBookings/useJobBookingBookFlow';
import usePacPalletJobBookingView from '@/composables/views/usePacPalletJobBookingView';
import { useDialogStore } from '@/stores/useDialogStore';

const {
  customer,
  stops,
  products,
  attachments,
  refConfigs,
  addressItems,
  addressLoading,
  isSubmitting,
  bookingErrors,
  draftId,
  draftName,
  addProduct,
  removeProduct,
  submit,
  reset,
  saveDraft,
  goToDraftsList,
} = usePacPalletJobBookingView();

const dialogStore = useDialogStore();

const form = ref<InstanceType<typeof VForm> | null>(null);
const errorPanel = ref<InstanceType<typeof JobBookingErrorPanel> | null>(null);

const { handleBook, handleBookMenuSelect, handleClearForm, handleSaveDraft } =
  useJobBookingBookFlow({
    form,
    errorPanel,
    stops,
    submit,
    reset,
    saveDraft,
    draftId,
    draftName,
    dialogStore,
  });

const bookMenuItems: SplitBtnItem[] = [
  {
    title: 'Book & Duplicate',
    value: 'duplicate',
    prependIcon: 'mdi-content-copy',
  },
  {
    title: 'Book & Create New Job',
    value: 'createNew',
    prependIcon: 'mdi-file-plus-outline',
  },
];
</script>

<template>
  <PageContentWrapper>
    <v-form ref="form" validate-on="submit lazy" @submit.prevent="handleBook">
      <div
        :class="$style.pacPalletJobBookingView"
        data-testid="pacPalletJobBookingView"
      >
        <AdvancedBookingFormSection label="Customer Details">
          <JobBookingCustomerSection
            v-model="customer"
            :ref-configs="refConfigs"
          />
        </AdvancedBookingFormSection>

        <JobBookingStopsSection
          v-model="stops"
          :customer-code="customer.customer_code ?? ''"
          :address-items="addressItems"
          :address-loading="addressLoading"
          :max-stops="2"
        />

        <JobBookingProductsSection @add-btn-click="addProduct">
          <PacPalletProductRow
            v-for="(product, index) in products"
            :key="index"
            v-model="products[index]"
            :is-deletable="products.length > 1"
            @delete-btn-click="removeProduct(index)"
          />
        </JobBookingProductsSection>

        <AdvancedBookingFormSection label="Attachments">
          <DetailedJobAttachmentsDropZoneField
            v-model="attachments"
            variant="dropzone"
          />
        </AdvancedBookingFormSection>

        <JobBookingErrorPanel
          v-if="bookingErrors.length"
          ref="errorPanel"
          :errors="bookingErrors"
        />
      </div>
    </v-form>

    <JobBookingFooterBar
      :is-submitting="isSubmitting"
      @clear-form="handleClearForm"
    >
      <KBtn
        variant="outlined"
        priority="primary"
        :disabled="isSubmitting"
        @click="handleSaveDraft"
      >
        Save as draft
      </KBtn>
      <KBtn variant="outlined" priority="primary" @click="goToDraftsList">
        Drafts list
      </KBtn>
      <KSplitBtn
        label="Book job"
        :items="bookMenuItems"
        :loading="isSubmitting"
        @click="handleBook"
        @select="handleBookMenuSelect"
      />
    </JobBookingFooterBar>
  </PageContentWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
.pacPalletJobBookingView {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1440px;
}
</style>
